import { Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { Heart, Tag } from "lucide-react";
import { formatGBP } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { ProductBadges } from "@/components/ProductBadges";
import { colourHex } from "@/lib/productOptions";

export const ProductCard = ({ product }) => {
  const { wishlist, toggleWishlist } = useCart();
  const { format } = useCurrency();
  // Only devices that actually support hover (desktop) get the hover-swap media.
  const [canHover, setCanHover] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setCanHover(window.matchMedia("(hover: hover) and (pointer: fine)").matches);
  }, []);
  // Catch images that are already cached (onLoad may not fire for those).
  const markLoaded = useCallback((node) => {
    if (node && node.complete && node.naturalWidth > 0) setLoaded(true);
  }, []);
  const wished = wishlist.includes(product.id);
  const stockLeft = product.stock_left;
  const soldOut = stockLeft === 0;
  const lowStock = typeof stockLeft === "number" && stockLeft > 0 && stockLeft <= 2;

  // Retry a failed image load once (transient proxy hiccup) before giving up.
  const handleImgError = (e) => {
    const img = e.currentTarget;
    if (img.dataset.retried) return;
    img.dataset.retried = "1";
    const base = img.src.split("?")[0];
    setTimeout(() => { img.src = `${base}?r=${Date.now()}`; }, 600);
  };
  const hoverVideo = canHover ? (product.hover_video || null) : null;
  const hoverImg = canHover && product.hover_image && product.hover_image !== product.image ? product.hover_image : null;
  const hasHover = !!(hoverVideo || hoverImg);
  const colours = product.colors || [];
  const shownColours = colours.slice(0, 5);
  const extraColours = colours.length - shownColours.length;

  return (
    <div className="flex flex-col" data-testid={`product-card-wrap-${product.slug}`}>
      {lowStock && (
        <div
          data-testid={`low-stock-tag-${product.slug}`}
          className="text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5a4410] py-1.5"
          style={{ background: "linear-gradient(135deg, #cfa64a 0%, #f4e2a4 45%, #e0bf55 70%, #cfa64a 100%)" }}
        >
          Only {stockLeft} Left in Stock
        </div>
      )}
      <div
        className="group flex flex-col bg-white border border-gray-100 hover:shadow-lg transition-shadow duration-300"
        data-testid={`product-card-${product.slug}`}
      >
        <div className="relative aspect-[3/4] overflow-hidden bg-surface">
          {!loaded && (
            <div className="absolute inset-0 z-[1] bg-surface overflow-hidden" aria-hidden="true">
              <div className="pk-shimmer absolute inset-0" />
            </div>
          )}
          <Link to={`/product/${product.slug}`}>
            <img
              ref={markLoaded}
              src={product.image}
              alt={product.name}
              loading="lazy"
              decoding="async"
              onLoad={() => setLoaded(true)}
              onError={handleImgError}
              className={`absolute inset-0 w-full h-full object-cover ease-out transition-[opacity,transform,filter] duration-700 group-hover:scale-105 ${
                !loaded ? "opacity-0 blur-md scale-[1.03]" : soldOut ? "opacity-60 blur-0" : "opacity-100 blur-0"
              } ${hasHover && !soldOut ? "group-hover:opacity-0" : ""}`}
            />
            {hoverVideo ? (
              <video
                src={hoverVideo}
                muted
                loop
                playsInline
                preload="metadata"
                data-testid={`product-hover-video-${product.slug}`}
                onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                onMouseLeave={(e) => { e.currentTarget.pause(); }}
                className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100"
              />
            ) : hoverImg && (
              <img
                src={hoverImg}
                alt={`${product.name} alternate view`}
                loading="lazy"
                decoding="async"
                onError={handleImgError}
                data-testid={`product-hover-image-${product.slug}`}
                className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100"
              />
            )}
          </Link>

          <ProductBadges
            product={product}
            className="absolute top-2.5 left-2.5 right-12 z-10 flex-col !items-start gap-1.5"
          />

          {product.on_sale && (
            <span className="absolute bottom-3 left-3 z-10 bg-pink text-white text-[10px] uppercase tracking-widest px-2.5 py-1">
              Sale
            </span>
          )}

          <button
            onClick={() => toggleWishlist(product)}
            aria-label="Add to wishlist"
            data-testid={`wishlist-btn-${product.slug}`}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center hover:scale-110 transition-transform duration-300"
          >
            <Heart className={`w-4 h-4 transition-colors ${wished ? "fill-pink text-pink" : "text-gray-500"}`} />
          </button>

          {colours.length > 0 && (
            <div
              className="absolute top-1/2 -translate-y-1/2 right-2.5 z-10 flex flex-col items-center gap-1.5"
              data-testid={`card-colours-${product.slug}`}
            >
              {shownColours.map((c) => (
                <span
                  key={c}
                  title={c}
                  data-testid={`card-colour-${product.slug}-${c}`}
                  className="w-3.5 h-3.5 rounded-full ring-1 ring-black/15 shadow-sm"
                  style={{ backgroundColor: colourHex(c) }}
                />
              ))}
              {extraColours > 0 && (
                <span
                  title={`${extraColours} more colour${extraColours > 1 ? "s" : ""}`}
                  className="w-4 h-4 rounded-full bg-white/95 text-charcoal text-[8px] font-semibold flex items-center justify-center shadow-sm ring-1 ring-black/5"
                >
                  +{extraColours}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="p-3 sm:p-4 text-center flex flex-col items-center">
          <Link to={`/product/${product.slug}`}>
            <h3 className="font-clean font-bold text-sm sm:text-[15px] text-charcoal leading-snug">{product.name}</h3>
            <p className="font-clean text-xs text-gray-500 mt-1">{product.subtitle}</p>
          </Link>
          <div className="mt-2 flex items-center justify-center gap-2 font-clean">
            <span className="text-[15px] font-bold text-charcoal">{format(product.price)}</span>
            {product.original_price && (
              <span className="text-xs text-gray-400 line-through">{format(product.original_price)}</span>
            )}
          </div>
          {product.original_price && product.original_price > product.price && (
            <div
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-pink"
              data-testid={`discount-percent-${product.slug}`}
            >
              <Tag className="w-3 h-3" /> {Math.round((1 - product.price / product.original_price) * 100)}% OFF
            </div>
          )}
          {soldOut ? (
            <div
              data-testid={`sold-out-${product.slug}`}
              className="mt-3 w-full rounded-full border border-gray-200 bg-gray-100 font-sans text-[11px] tracking-wide text-gray-400 py-2.5 text-center cursor-not-allowed select-none"
            >
              Sold Out
            </div>
          ) : (
            <Link
              to={`/product/${product.slug}`}
              data-testid={`select-options-${product.slug}`}
              className="mt-3 w-full rounded-full border border-charcoal/15 font-sans text-[11px] tracking-wide text-charcoal py-2.5 text-center hover:bg-pink hover:border-pink hover:text-white transition-colors duration-300"
            >
              Select options
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
